import { Fragment } from "react";
import {
  BarChart3,
  ChartColumnIncreasing,
  MessageSquare,
  Mic,
  Activity,
  FileText,
  FlaskConical,
  Flag,
  Users,
  Building2,
  School,
  LogOut,
  Layers,
  BookOpen,
  Package,
  Database,
  AudioLines,
  CalendarClock,
  Link as LinkIcon,
  Swords,
  AudioWaveform,
  KeyRound,
  type LucideIcon,
} from "lucide-react";
import {
  TAB_REGISTRY,
  TAB_AUDIENCES,
  isNavItemVisible,
  type TabKey,
} from "../lib/tab-registry";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

type Tab = TabKey;

// Only icons live here; ids, labels, and shortcuts come from the registry.
const iconByTab: Record<Tab, LucideIcon> = {
  usage: BarChart3,
  conversations: MessageSquare,
  recordings: Mic,
  analytics: ChartColumnIncreasing,
  loadtest: Activity,
  classroomreports: School,
  prompts: FileText,
  experiments: FlaskConical,
  tutorversions: Package,
  questionbank: Database,
  ttsarena: Swords,
  sttarena: AudioWaveform,
  classrooms: School,
  curriculum: BookOpen,
  featureflags: Flag,
  users: Users,
  organizations: Building2,
  lessonconfig: BookOpen,
  ttspool: AudioLines,
  schedules: CalendarClock,
  cleverlms: LinkIcon,
  datafetch: Database,
  iam: KeyRound,
};

const isEvaluation = (tab: TabKey) => tab === "ttsarena" || tab === "sttarena";

const toNavItem = (def: (typeof TAB_REGISTRY)[number]) => ({
  id: def.id as Tab,
  label: isEvaluation(def.id) ? "Speech evaluation" : ("navLabel" in def && def.navLabel) || def.label,
  icon: iconByTab[def.id],
  shortcut: def.shortcut.toUpperCase(),
});

type NavigationSection = { label: string; icon?: LucideIcon; items: ReturnType<typeof toNavItem>[] };
const navigationSections: NavigationSection[] = TAB_AUDIENCES.map((audience) => ({
  label: audience.label,
  items: TAB_REGISTRY.filter((tab) => tab.audience === audience.id).map(toNavItem),
}));

interface DashboardSidebarProps {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  user: string | null;
  allowedTabs: Tab[];
  onLogout: () => void;
}

export function DashboardSidebar({
  activeTab,
  onTabChange,
  user,
  allowedTabs,
  onLogout,
}: DashboardSidebarProps) {
  const visibleSections = navigationSections.map((section) => ({
    ...section,
    items: section.items.filter((item) => isNavItemVisible(allowedTabs, item.id)),
  })).filter((section) => section.items.length > 0);
  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-14 justify-center border-b border-sidebar-border px-3 py-2 group-data-[collapsible=icon]:px-2">
        <div className="flex items-center gap-3 group-data-[collapsible=icon]:justify-center">
          <div className="flex size-9 items-center justify-center rounded-xl bg-gradient-to-br from-lime-600 via-green-600 to-emerald-600 text-white shadow-[0_10px_20px_-12px_rgba(132,204,22,0.65)] ring-1 ring-lime-600/20">
            <Layers className="size-4" />
          </div>
          <div className="flex min-w-0 flex-1 items-center justify-between gap-2 leading-tight group-data-[collapsible=icon]:hidden">
            <div className="min-w-0">
              <span className="truncate text-sm font-semibold">
                ET Dashboard
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                Internal tools
              </span>
            </div>
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {visibleSections.map((section, index) => (
          <Fragment key={section.label}>
            {index > 0 && <SidebarSeparator className="my-1" />}
            <SidebarGroup className="py-3">
              <SidebarGroupLabel className="flex items-center gap-2 uppercase tracking-[0.12em] text-[10px]">
                {section.icon && <section.icon className="size-3.5" />}
                {section.label}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="gap-1.5">
              {section.items.map((item) => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    isActive={activeTab === item.id || (isEvaluation(item.id) && isEvaluation(activeTab))}
                    onClick={() => onTabChange(item.id)}
                    tooltip={item.label}
                    size="lg"
                    className="gap-3 rounded-xl px-3 text-[13px] transition-all duration-150 hover:scale-[1.01] hover:shadow-sm data-[active=true]:bg-lime-600/10 data-[active=true]:text-lime-900 data-[active=true]:shadow-[0_6px_16px_-12px_rgba(132,204,22,0.8)] data-[active=true]:ring-1 data-[active=true]:ring-lime-600/15 group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:rounded-2xl group-data-[collapsible=icon]:px-0"
                  >
                    <item.icon className="size-4" />
                    <span className="flex-1 group-data-[collapsible=icon]:hidden">
                      {item.label}
                    </span>
                    <kbd className="hidden rounded border bg-muted/60 px-1.5 py-0.5 text-[10px] text-muted-foreground group-data-[collapsible=icon]:hidden lg:inline-block">
                      {item.shortcut}
                    </kbd>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </Fragment>
        ))}
      </SidebarContent>
      <SidebarFooter className="gap-3">
        <SidebarSeparator className="my-1" />
        <div className="flex items-center gap-3 rounded-xl bg-sidebar-accent/40 px-2.5 py-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:bg-transparent">
          <Avatar className="size-9">
            <AvatarFallback className="bg-lime-600/15 text-lime-900 text-sm">
              {user?.charAt(0).toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
            <p className="truncate text-sm font-medium text-sidebar-foreground">
              {user || "Unknown user"}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {`${allowedTabs.length} ${allowedTabs.length === 1 ? "tab" : "tabs"} granted`}
            </p>
          </div>
        </div>
        <Button
          variant="secondary"
          className="mx-2 mb-1 h-10 gap-2 rounded-xl border border-lime-600/20 bg-lime-600/10 text-lime-900 hover:bg-lime-600/20 group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:size-10 group-data-[collapsible=icon]:p-0"
          onClick={onLogout}
        >
          <LogOut className="size-4" />
          <span className="group-data-[collapsible=icon]:hidden">Sign out</span>
        </Button>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
